import { z } from 'zod/v4';

/** An address as amfori or the supplier's profile gives it; any part may be missing. */
export const AmforiSiteAddressSchema = z
  .object({
    street: z.string().nullable(),
    city: z.string().nullable(),
    zip: z.string().nullable(),
    country: z.string().nullable(),
  })
  .strict();

export type AmforiSiteAddress = z.infer<typeof AmforiSiteAddressSchema>;

/** The amfori record of the checked site, as the acting brand's amfori data has it. */
export const AmforiCheckedSiteSchema = z
  .object({
    siteId: z.string(),
    name: z.string().nullable(),
    businessPartnerId: z.string(),
    businessPartnerName: z.string().nullable(),
    address: AmforiSiteAddressSchema,
    auditCount: z.number(),
  })
  .strict();

export type AmforiCheckedSite = z.infer<typeof AmforiCheckedSiteSchema>;

/**
 * One warning of the check (DELTA-2763), with the values the dialog shows.
 * Each must be acknowledged before the link is written; none blocks it.
 *
 * - `NEW_PARTNER` — the site belongs to another business partner than the
 *   supplier's: linking it switches the partner and removes the listed sites.
 * - `COUNTRY_MISMATCH` — the site is in another country than the supplier.
 * - `ADDRESS_DIFFERENT` — the site's city or street matches neither the
 *   supplier's address nor any site it holds.
 * - `NAME_DIFFERENT` — the site's name does not match the supplier's.
 * - `NO_AUDITS` — amfori lists no audit for the site.
 * - `BETTER_CANDIDATE` — the site has no audits, and the brand's amfori data
 *   has a site of the same name that does: likely the record to link instead.
 */
export const AmforiSiteCheckWarningSchema = z.discriminatedUnion('code', [
  z
    .object({
      code: z.literal('NEW_PARTNER'),
      fromBusinessPartnerId: z.string(),
      toBusinessPartnerId: z.string(),
      removedSiteIds: z.array(z.string()),
    })
    .strict(),
  z
    .object({
      code: z.literal('COUNTRY_MISMATCH'),
      siteCountry: z.string(),
      supplierCountry: z.string(),
    })
    .strict(),
  z
    .object({
      code: z.literal('ADDRESS_DIFFERENT'),
      siteAddress: AmforiSiteAddressSchema,
      supplierAddress: AmforiSiteAddressSchema,
    })
    .strict(),
  z
    .object({
      code: z.literal('NAME_DIFFERENT'),
      siteName: z.string(),
      supplierName: z.string(),
    })
    .strict(),
  z.object({ code: z.literal('NO_AUDITS') }).strict(),
  z
    .object({
      code: z.literal('BETTER_CANDIDATE'),
      candidate: AmforiCheckedSiteSchema,
    })
    .strict(),
]);

export type AmforiSiteCheckWarning = z.infer<
  typeof AmforiSiteCheckWarningSchema
>;

/**
 * Why a Site ID cannot be linked at all:
 *
 * - `NOT_CONNECTED` — the acting brand has no working amfori connection, so
 *   the site cannot be looked up.
 * - `NOT_IN_SUPPLY_CHAIN` — the site is not in the acting brand's amfori data.
 * - `HELD_ELSEWHERE` — another supplier on the platform holds it (a Site ID
 *   belongs to one supplier, DELTA-2764).
 */
export const AmforiSiteCheckBlockerSchema = z.enum([
  'NOT_CONNECTED',
  'NOT_IN_SUPPLY_CHAIN',
  'HELD_ELSEWHERE',
]);

export type AmforiSiteCheckBlocker = z.infer<
  typeof AmforiSiteCheckBlockerSchema
>;

/**
 * How the site relates to the supplier's link: its first site, another site
 * of its business partner, one it already holds, or a site of another partner
 * (linking it switches the partner).
 */
export const AmforiSiteCheckRelationSchema = z.enum([
  'FIRST',
  'SAME_PARTNER',
  'ALREADY',
  'NEW_PARTNER',
]);

export type AmforiSiteCheckRelation = z.infer<
  typeof AmforiSiteCheckRelationSchema
>;

/**
 * The check of a Site ID before linking it to a supplier (DELTA-2763):
 * the amfori record next to the supplier's profile, and what stands in the
 * way. `site` is `null` when the brand's amfori data could not be read or
 * does not have the site. `heldByName` names the supplier holding the site
 * only when it is in the brand's network.
 */
export const AmforiSiteCheckResultSchema = z
  .object({
    siteId: z.string(),
    site: AmforiCheckedSiteSchema.nullable(),
    supplier: z
      .object({
        name: z.string().nullable(),
        address: AmforiSiteAddressSchema,
      })
      .strict(),
    relation: AmforiSiteCheckRelationSchema,
    blocker: AmforiSiteCheckBlockerSchema.nullable(),
    heldByName: z.string().nullable(),
    warnings: z.array(AmforiSiteCheckWarningSchema),
  })
  .strict();

export type AmforiSiteCheckResult = z.infer<typeof AmforiSiteCheckResultSchema>;
